import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Link,
  Paper,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { formatDateWithoutTime } from "../../../../../../views/components/common/functions/formatDate";
import {
  ProductCommentAttachmentDrawerDto,
  ProductCommentDrawerDto,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../styles/modernListingPageSx";
import { filenameFromUrl } from "./DailyTasksMedia";
import { DailyTasksEmptyState } from "./DailyTasksEmptyState";

interface ProductCommentsSectionProps {
  comments: ProductCommentDrawerDto[] | undefined | null;
}

const AVATAR_COLORS = [
  "#5E35B1",
  "#1E88E5",
  "#00897B",
  "#E53935",
  "#FB8C00",
  "#8E24AA",
  "#3949AB",
  "#00ACC1",
];

const getInitials = (name?: string | null): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const avatarColor = (name?: string | null): string => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  return formatDateWithoutTime(value) || "-";
};

const countComments = (nodes: ProductCommentDrawerDto[]): number => {
  let total = 0;
  for (const node of nodes) {
    total += 1;
    if (node.replies?.length) total += countComments(node.replies);
  }
  return total;
};

const attachmentLabel = (
  attachment: ProductCommentAttachmentDrawerDto,
): string =>
  attachment.file_name ||
  filenameFromUrl(attachment.file_path, `attachment-${attachment.id}`);

const isImageAttachment = (
  attachment: ProductCommentAttachmentDrawerDto,
): boolean => {
  const name = attachmentLabel(attachment).toLowerCase();
  return (
    /\.(png|jpe?g|webp|gif)$/i.test(name) ||
    /\.(png|jpe?g|webp|gif)(\?|$)/i.test(attachment.file_path)
  );
};

const AttachmentChip: React.FC<{
  attachment: ProductCommentAttachmentDrawerDto;
}> = ({ attachment }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const label = attachmentLabel(attachment);
  const isImage = isImageAttachment(attachment);

  return (
    <Link
      href={attachment.file_path}
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      sx={{ display: "inline-flex", maxWidth: "100%" }}
    >
      <Chip
        size="small"
        icon={
          isImage ? (
            <AttachFileOutlinedIcon sx={{ fontSize: "14px !important" }} />
          ) : (
            <InsertDriveFileOutlinedIcon sx={{ fontSize: "14px !important" }} />
          )
        }
        label={label}
        sx={{
          maxWidth: "100%",
          height: 26,
          borderRadius: 1.5,
          bgcolor: alpha(colors.purple, 0.08),
          color: colors.purple,
          border: `1px solid ${alpha(colors.purple, 0.18)}`,
          "& .MuiChip-icon": { color: colors.purple },
          "& .MuiChip-label": {
            fontWeight: 500,
            fontSize: "0.72rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "&:hover": {
            bgcolor: alpha(colors.purple, 0.14),
          },
        }}
      />
    </Link>
  );
};

const CommentNode: React.FC<{
  comment: ProductCommentDrawerDto;
  depth: number;
}> = ({ comment, depth }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const pageColors = getModernPageColors(theme);
  const isDeleted = Boolean(comment.deleted_at);
  const author = comment.author_name || "Unknown";
  const bodyText = isDeleted
    ? "This comment was deleted"
    : comment.body?.trim() || "";
  const hasAttachments = !isDeleted && (comment.attachments?.length ?? 0) > 0;
  const showEmptyBody = !bodyText && !hasAttachments && !isDeleted;

  return (
    <Box sx={{ mb: depth === 0 ? 1.5 : 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          ml: depth > 0 ? Math.min(depth, 4) * 1.5 : 0,
        }}
      >
        {depth > 0 ? (
          <Box
            sx={{
              width: 12,
              flexShrink: 0,
              mt: 1.5,
              mr: -0.25,
              borderLeft: `2px solid ${pageColors.border}`,
              borderBottom: `2px solid ${pageColors.border}`,
              borderBottomLeftRadius: 6,
              height: 14,
            }}
          />
        ) : null}

        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: "0.65rem",
            fontWeight: 700,
            flexShrink: 0,
            mt: 0.15,
            bgcolor: isDeleted ? theme.palette.grey[400] : avatarColor(author),
          }}
        >
          {getInitials(author)}
        </Avatar>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            border: `1px solid ${
              isDeleted ? pageColors.border : alpha(pageColors.border, 0.95)
            }`,
            borderRadius: 2,
            bgcolor: isDeleted
              ? alpha(theme.palette.grey[500], 0.06)
              : colors.white,
            px: 1.25,
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              flexWrap: "wrap",
              gap: 0.75,
              mb: 0.5,
            }}
          >
            <CustomTypography
              variant="body3"
              fontWeight={600}
              sx={{ color: pageColors.title }}
            >
              {author}
            </CustomTypography>
            <CustomTypography
              variant="caption"
              sx={{ color: pageColors.subtitle }}
            >
              {formatDate(comment.created_at)}
            </CustomTypography>
            {isDeleted ? (
              <Chip
                size="small"
                label="Deleted"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.grey[600], 0.1),
                  color: theme.palette.grey[700],
                }}
              />
            ) : null}
            {depth > 0 && !isDeleted ? (
              <Chip
                size="small"
                label="Reply"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  bgcolor: alpha(colors.purple, 0.08),
                  color: colors.purple,
                }}
              />
            ) : null}
          </Box>

          {bodyText || showEmptyBody ? (
            <CustomTypography
              variant="body3"
              fontWeight={500}
              sx={{
                color: isDeleted
                  ? pageColors.subtitle
                  : colors.mutedSlateText || pageColors.title,
                fontStyle: isDeleted ? "italic" : "normal",
                display: "block",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                lineHeight: 1.45,
              }}
            >
              {showEmptyBody ? "—" : bodyText}
            </CustomTypography>
          ) : null}

          {hasAttachments ? (
            <Box
              sx={{
                mt: bodyText ? 1 : 0.25,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              {comment.attachments.map((attachment) => (
                <AttachmentChip key={attachment.id} attachment={attachment} />
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>

      {(comment.replies ?? []).map((reply) => (
        <CommentNode key={reply.id} comment={reply} depth={depth + 1} />
      ))}
    </Box>
  );
};

const ProductCommentsSection: React.FC<ProductCommentsSectionProps> = ({
  comments,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const pageColors = getModernPageColors(theme);
  const roots = comments ?? [];
  const total = countComments(roots);

  return (
    <Paper elevation={0} sx={{ ...modernSectionPaperSx(theme), mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: total === 0 ? 1.25 : 1.5,
        }}
      >
        <CustomTypography
          variant="heading7"
          fontWeight={600}
          sx={{ color: pageColors.title }}
        >
          Product comments
        </CustomTypography>
        <Chip
          size="small"
          label={total}
          sx={{
            height: 22,
            minWidth: 28,
            fontWeight: 600,
            fontSize: "0.75rem",
            bgcolor:
              total === 0
                ? alpha(theme.palette.grey[600], 0.1)
                : alpha(colors.purple, 0.1),
            color: total === 0 ? theme.palette.grey[700] : colors.purple,
          }}
        />
      </Box>

      {total === 0 ? (
        <DailyTasksEmptyState
          inPaper={false}
          icon={ChatBubbleOutlineOutlinedIcon}
          title="No product comments yet"
          description="Comments added in sourcing ops for this product will show up here."
        />
      ) : (
        <Box>
          {roots.map((comment) => (
            <CommentNode key={comment.id} comment={comment} depth={0} />
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default ProductCommentsSection;
